export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const validateName = (name: string): { isValid: boolean; message?: string } => {
  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return { isValid: false, message: 'Name is required' };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, message: 'Name must be at least 2 characters' };
  }

  if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
    return { isValid: false, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
  }

  return { isValid: true };
};

export interface PasswordRequirement {
  label: string;
  met: boolean;
  regex?: RegExp;
  test: (password: string) => boolean;
}

export const getPasswordRequirements = (password: string): PasswordRequirement[] => {
  return [
    {
      label: 'At least 8 characters',
      met: password.length >= 8,
      test: (pwd: string) => pwd.length >= 8,
    },
    {
      label: 'One uppercase letter (A-Z)',
      met: /[A-Z]/.test(password),
      regex: /[A-Z]/,
      test: (pwd: string) => /[A-Z]/.test(pwd),
    },
    {
      label: 'One lowercase letter (a-z)',
      met: /[a-z]/.test(password),
      regex: /[a-z]/,
      test: (pwd: string) => /[a-z]/.test(pwd),
    },
    {
      label: 'One number (0-9)',
      met: /[0-9]/.test(password),
      regex: /[0-9]/,
      test: (pwd: string) => /[0-9]/.test(pwd),
    },
    {
      label: 'One special character (!@#$%...)',
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      regex: /[!@#$%^&*(),.?":{}|<>]/,
      test: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    },
  ];
};

export const validatePassword = (password: string): {
  isValid: boolean;
  strength: 'weak' | 'medium' | 'strong';
  messages: string[];
  requirements: PasswordRequirement[];
} => {
  const requirements = getPasswordRequirements(password);
  const messages: string[] = [];
  let strength: 'weak' | 'medium' | 'strong' = 'weak';

  requirements.forEach((req) => {
    if (!req.met) {
      messages.push(req.label);
    }
  });

  const isValid = messages.length === 0;

  if (isValid) {
    strength = 'strong';
  } else if (password.length >= 8 && messages.length <= 2) {
    strength = 'medium';
  }

  return { isValid, strength, messages, requirements };
};

export const validateUKPhone = (phone: string): boolean => {
  const phoneRegex = /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|\#)\d{3,4})?$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const validateUKPostcode = (postcode: string): boolean => {
  const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  return postcodeRegex.test(postcode.trim());
};

export const formatUKPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return cleaned.replace(/(\d{5})(\d{6})/, '$1 $2');
  }

  return phone;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const validatePostTitle = (title: string): { isValid: boolean; message?: string } => {
  const trimmedTitle = title.trim();

  if (trimmedTitle.length === 0) {
    return { isValid: false, message: 'Title is required' };
  }

  if (trimmedTitle.length < 10) {
    return { isValid: false, message: 'Title must be at least 10 characters' };
  }

  if (trimmedTitle.length > 100) {
    return { isValid: false, message: 'Title must not exceed 100 characters' };
  }

  return { isValid: true };
};

export const validatePostDescription = (description: string): { isValid: boolean; message?: string } => {
  const trimmedDescription = description.trim();

  if (trimmedDescription.length === 0) {
    return { isValid: false, message: 'Description is required' };
  }

  if (trimmedDescription.length < 50) {
    return { isValid: false, message: 'Description must be at least 50 characters for providers to understand your needs' };
  }

  if (trimmedDescription.length > 1000) {
    return { isValid: false, message: 'Description must not exceed 1000 characters' };
  }

  return { isValid: true };
};

export const validateBudget = (budget: number): { isValid: boolean; message?: string } => {
  if (isNaN(budget) || budget <= 0) {
    return { isValid: false, message: 'Budget must be a positive number' };
  }

  if (budget < 20) {
    return { isValid: false, message: 'Budget must be at least £20' };
  }

  if (budget > 10000) {
    return { isValid: false, message: 'Budget must not exceed £10,000' };
  }

  return { isValid: true };
};

export const validateFutureDate = (dateString: string): { isValid: boolean; message?: string } => {
  const appointmentDate = new Date(dateString);
  const now = new Date();
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(now.getFullYear() + 1);

  if (isNaN(appointmentDate.getTime())) {
    return { isValid: false, message: 'Invalid date format' };
  }

  if (appointmentDate <= now) {
    return { isValid: false, message: 'Appointment date must be in the future' };
  }

  if (appointmentDate > oneYearFromNow) {
    return { isValid: false, message: 'Appointment date must be within the next year' };
  }

  return { isValid: true };
};

export const validateImageFile = (file: File): { isValid: boolean; message?: string } => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSizeInBytes = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return { isValid: false, message: 'Only JPG, PNG, and WebP images are allowed' };
  }

  if (file.size > maxSizeInBytes) {
    return { isValid: false, message: 'Image must be smaller than 5MB' };
  }

  return { isValid: true };
};
